import { alpha } from '@mui/material';
import { blue } from '@mui/material/colors';

import {
  BOUNDING_BOX_POP_UP_BOX_POSITION,
  PDF_PAGE_RENDER_WINDOW_RADIUS,
} from './DocumentView.constants';

const BOUNDING_BOX_FILL_ALPHA = 0.5;

export const getThumbnailAnchorId = idx => `document-thumbnail-page-anchor-${idx}`;

export const checkIsPageInRenderWindow = (displayIdx, readingPageIndex) =>
  Number.isFinite(readingPageIndex) &&
  Math.abs(displayIdx - readingPageIndex) <= PDF_PAGE_RENDER_WINDOW_RADIUS;

const getInitialReadingPageIndex = initialSelectedPage => (initialSelectedPage ?? 1) - 1;

export const getInitialReadingAnchor = initialSelectedPage =>
  initialSelectedPage ? { readingPageIndex: initialSelectedPage - 1, relativeOffset: 0 } : null;

export const getUnloadedDocumentState = (prevState, initialSelectedPage) => ({
  ...prevState,
  pdf: null,
  pageBaseSizes: null,
  readingPageIndex: getInitialReadingPageIndex(initialSelectedPage),
  pageOrder: null,
  isReorderSaving: false,
});

export const getReadingPageState = (prevState, readingPageIndex) =>
  prevState.readingPageIndex === readingPageIndex ? prevState : { ...prevState, readingPageIndex };

export const getScrolledPageState = (prevState, { pageNumber, isSelectionChanged }) =>
  isSelectionChanged
    ? { ...prevState, readingPageIndex: pageNumber - 1, selectedPage: [pageNumber] }
    : getReadingPageState(prevState, pageNumber - 1);

export const getScrollInContainerToPage = containerRef => pageNumber => {
  const container = containerRef.current;
  const element = container?.childNodes?.[pageNumber - 1];

  if (!container || !pageNumber || !element) {
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  container.scrollTop = elementRect.top - containerRect.top + container.scrollTop;
};

export const getMostVisiblePageNumber = container => {
  const pages = container?.childNodes;

  if (!pages?.length) {
    return null;
  }

  const { scrollTop, scrollHeight, clientHeight } = container;

  if (scrollTop <= 0) {
    return 1;
  }

  if (scrollTop + clientHeight >= scrollHeight - 1) {
    return pages.length;
  }

  const containerRect = container.getBoundingClientRect();

  const { index } = Array.from(pages).reduce(
    (acc, pageNode, pageIndex) => {
      const rect = pageNode.getBoundingClientRect();
      const visibleTop = Math.max(rect.top, containerRect.top);
      const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      return visibleHeight > acc.height ? { index: pageIndex, height: visibleHeight } : acc;
    },
    { index: 0, height: 0 },
  );

  return index + 1;
};

// Offsets are relative to the pages container, which is the pages' offsetParent.
export const getReadingAnchor = container => {
  const pageNumber = getMostVisiblePageNumber(container);
  const page = container?.childNodes?.[pageNumber - 1];

  if (!page?.offsetHeight) {
    return null;
  }

  return {
    readingPageIndex: pageNumber - 1,
    relativeOffset: (container.scrollTop - page.offsetTop) / page.offsetHeight,
  };
};

export const getAnchoredScrollTop = (container, { readingPageIndex, relativeOffset }) => {
  const page = container?.childNodes?.[readingPageIndex];

  if (!page) {
    return null;
  }

  return page.offsetTop + relativeOffset * page.offsetHeight;
};

const drawBoundingBox = ({
  context,
  coordX,
  coordY,
  rectangleWidth,
  rectangleHeight,
  isMouseInBoundingBox,
  boundingBoxColors,
}) => {
  const { stroke, strokeHovered, fill } = boundingBoxColors;
  context.strokeStyle = isMouseInBoundingBox ? strokeHovered : stroke;
  context.fillStyle = fill;

  context.beginPath();
  context.rect(coordX, coordY, rectangleWidth, rectangleHeight);
  context.stroke();

  if (isMouseInBoundingBox) {
    context.fill();
  }
};

const getBoundingBoxCoordsInPixels = ({
  aiModelPageWidth,
  aiModelPageHeigth,
  pageWidth,
  pageHeight,
  coordX,
  coordY,
}) => {
  const coordXInPixels = (coordX / aiModelPageWidth) * pageWidth;
  const coordYInPixels = (coordY / aiModelPageHeigth) * pageHeight;

  return {
    coordX: coordXInPixels,
    coordY: coordYInPixels,
  };
};

const getBoundingBoxSize = ({ upperLeft, upperRight, bottomRight }) => {
  const widthAdjustment = 2;
  const width = upperRight.coordX - upperLeft.coordX + widthAdjustment;
  const height = bottomRight.coordY - upperRight.coordY;

  return {
    width,
    height,
  };
};

export const displayFieldBoundingBox = ({
  boundingPolygon,
  pdfCard,
  context,
  isMouseInBoundingBox,
  pageNumber,
  boundingBoxColors,
  pageSize,
}) => {
  if (!boundingPolygon?.length) {
    return;
  }

  const { upperLeft, upperRight, bottomRight } = getBoundingBoxCoords({
    boundingPolygon,
    pdfCard,
    pageNumber,
    pageSize,
  });

  const boundingBoxSize = getBoundingBoxSize({ upperRight, upperLeft, bottomRight });

  drawBoundingBox({
    coordX: upperLeft.coordX,
    coordY: upperLeft.coordY,
    rectangleWidth: boundingBoxSize.width,
    rectangleHeight: boundingBoxSize.height,
    context,
    isMouseInBoundingBox,
    boundingBoxColors,
  });
};

export const getBoundingBoxCoords = ({ boundingPolygon, pdfCard, pageNumber, pageSize }) => {
  const boundingBoxCoords = boundingPolygon?.reduce((acc, { x, y }, index) => {
    const coordsInPixels = getBoundingBoxCoordsInPixels({
      aiModelPageWidth: pageSize.width,
      aiModelPageHeigth: pageSize.height,
      pageWidth: pdfCard.clientWidth,
      pageHeight: pdfCard.clientHeight,
      coordX: x,
      coordY: y,
      pageNumber,
    });

    return { ...acc, [`coord${index + 1}`]: coordsInPixels };
  }, {});

  const {
    coord1: upperLeft,
    coord2: upperRight,
    coord3: bottomRight,
    coord4: bottomLeft,
  } = boundingBoxCoords;

  return {
    upperLeft,
    upperRight,
    bottomRight,
    bottomLeft,
  };
};

export const checkIsMouseInsideBoundingBox = ({
  mouseX,
  mouseY,
  boundingPolygon,
  pdfCard,
  pageSize,
}) => {
  const boundingBoxCoords = boundingPolygon.map(({ x, y }) =>
    getBoundingBoxCoordsInPixels({
      aiModelPageWidth: pageSize.width,
      aiModelPageHeigth: pageSize.height,
      pageWidth: pdfCard.clientWidth,
      pageHeight: pdfCard.clientHeight,
      coordX: x,
      coordY: y,
    }),
  );

  const isMouseInBoundingBox =
    mouseX >= boundingBoxCoords[0].coordX &&
    mouseX <= boundingBoxCoords[1].coordX &&
    mouseY >= boundingBoxCoords[0].coordY &&
    mouseY <= boundingBoxCoords[2].coordY;

  return !!isMouseInBoundingBox;
};

export const getBoxQuadrant = ({ x, y, pdfCard }) => {
  if (!pdfCard) {
    return null;
  }

  const { width, height } = pdfCard.getBoundingClientRect();
  const centerX = width / 2;
  const centerY = height / 2;

  if (x > centerX) {
    return y >= centerY
      ? BOUNDING_BOX_POP_UP_BOX_POSITION.BOTTOM_RIGHT
      : BOUNDING_BOX_POP_UP_BOX_POSITION.UPPER_RIGHT;
  }

  if (x <= centerX) {
    return y >= centerY
      ? BOUNDING_BOX_POP_UP_BOX_POSITION.BOTTOM_LEFT
      : BOUNDING_BOX_POP_UP_BOX_POSITION.UPPER_LEFT;
  }
};

// The popup is positioned absolute relative to its offset parent (the page card), which itself
// scrolls inside a clipped viewport — a quadrant chosen to avoid the page-card edge can still
// land outside the visible, clipping ancestor. Clamp the resolved position back into view.
const clampBoundingBoxPopUpToViewport = popUp => {
  if (!popUp) {
    return;
  }

  const { offsetParent } = popUp;

  if (!offsetParent) {
    return;
  }

  const clipContainer = popUp.closest('.document_view__document_pages_wrapper') ?? offsetParent;
  const parentRect = offsetParent.getBoundingClientRect();
  const clipRect = clipContainer.getBoundingClientRect();
  const popUpRect = popUp.getBoundingClientRect();

  const minLeft = clipRect.left - parentRect.left;
  const maxLeft = clipRect.right - parentRect.left - popUpRect.width;
  const minTop = clipRect.top - parentRect.top;
  const maxTop = clipRect.bottom - parentRect.top - popUpRect.height;

  const currentLeft = popUpRect.left - parentRect.left;
  const currentTop = popUpRect.top - parentRect.top;

  popUp.style.left = `${Math.max(minLeft, Math.min(currentLeft, maxLeft))}px`;
  popUp.style.top = `${Math.max(minTop, Math.min(currentTop, maxTop))}px`;
};

export const setBoundingBoxPopUpPosition = ({ quadrant, coords, boundingBoxPopUp }) => {
  const popUp = boundingBoxPopUp?.current;
  const popupStyle = popUp?.style;
  const { width: popUpWidth, height: popUpHeight } = popUp.getBoundingClientRect();
  const { upperLeft, upperRight, bottomRight, bottomLeft } = coords;

  const offset = 5;

  switch (quadrant) {
    case BOUNDING_BOX_POP_UP_BOX_POSITION.UPPER_LEFT:
      popupStyle.left = `${bottomRight.coordX + offset}px`;
      popupStyle.top = `${bottomRight.coordY + offset}px`;
      break;

    case BOUNDING_BOX_POP_UP_BOX_POSITION.UPPER_RIGHT:
      popupStyle.left = `${bottomLeft.coordX - popUpWidth - offset}px`;
      popupStyle.top = `${bottomLeft.coordY + offset}px`;
      break;

    case BOUNDING_BOX_POP_UP_BOX_POSITION.BOTTOM_RIGHT:
      popupStyle.left = `${upperLeft.coordX - popUpWidth - offset}px`;
      popupStyle.top = `${upperLeft.coordY - popUpHeight - offset}px`;
      break;

    case BOUNDING_BOX_POP_UP_BOX_POSITION.BOTTOM_LEFT:
      popupStyle.left = `${upperRight.coordX + offset}px`;
      popupStyle.top = `${upperRight.coordY - popUpHeight - offset}px`;
      break;

    default:
      break;
  }

  clampBoundingBoxPopUpToViewport(popUp);
};

export const getBoundingBoxColors = ({ theme, isMappedOnExtractionField, isLayout = true }) => {
  const nonLayoutStroke = isMappedOnExtractionField ? blue[700] : theme.palette.error.main;
  const stroke = isLayout ? theme.palette.success.light : nonLayoutStroke;

  const nonLayoutStrokeHovered = isMappedOnExtractionField ? blue[900] : theme.palette.error.dark;
  const strokeHovered = isLayout ? theme.palette.success.main : nonLayoutStrokeHovered;

  const nonLayoutColorToFade = isMappedOnExtractionField ? blue[700] : theme.palette.error.light;
  const colorToFade = isLayout ? theme.palette.success.dark : nonLayoutColorToFade;

  const fill = alpha(colorToFade, BOUNDING_BOX_FILL_ALPHA);

  return {
    stroke,
    strokeHovered,
    fill,
  };
};

export const createLayoutFieldKey = (value, index) => `${value}-${index}`;
